import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import wMark from "@assets/W_1771840384312.webp";

interface NavbarProps {
  activePage?: "home" | "about" | "werkwijze" | "contact" | "cases" | "artikelen";
}

export default function Navbar({ activePage }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { t, toggleLanguage, language } = useLanguage();
  const nl = language === "nl";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const linkClass = (page: string) =>
    `text-[12px] font-semibold tracking-[0.14em] uppercase transition-colors ${
      activePage === page
        ? "text-white"
        : "text-white/70 hover:text-white"
    }`;

  const mobileLinkClass = (page: string) =>
    `text-lg tracking-wide uppercase transition-colors font-medium ${
      activePage === page
        ? "text-[#4CEAAC] font-semibold"
        : "text-white/70 hover:text-white"
    }`;

  const prefix = nl ? "" : "/en";
  const navItems = [
    { href: `${prefix}/cases`, label: t("nav_cases"), page: "cases" },
    { href: `${prefix}/about`, label: t("nav_about"), page: "about" },
    { href: `${prefix}/werkwijze`, label: t("nav_method"), page: "werkwijze" },
    { href: `${prefix}/contact`, label: t("nav_contact"), page: "contact" },
  ];

  return (
    <>
      <nav
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/60 backdrop-blur-xl border-b border-white/[0.08] py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 md:px-12 flex justify-between items-center">
          <Link href={`${prefix}/`} className="flex items-center gap-3">
            <img
              src={wMark}
              alt="Wrkt Digital logo"
              width={855}
              height={685}
              className="h-7 w-auto object-contain"
              data-testid="logo-wrkt"
            />
            <span className="hidden md:inline font-display font-extrabold text-[19px] tracking-[-0.02em] text-white">wrkt <span className="text-[#4BF4B9]">digital</span></span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <Link key={item.page} href={item.href} className={linkClass(item.page)}>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4 md:gap-5">
            <div
              onClick={toggleLanguage}
              className="hidden md:flex items-center gap-0.5 text-[11px] tracking-widest font-semibold cursor-pointer transition-colors uppercase"
            >
              <span className={nl ? "text-white" : "text-white/30"}>NL</span>
              <span className="text-white/20 mx-0.5">/</span>
              <span className={!nl ? "text-white" : "text-white/30"}>EN</span>
            </div>

            <button
              onClick={() => setLocation(`${prefix}/scan`)}
              className="btn btn--gradient btn--sm hidden md:inline-flex"
              data-testid="button-nav-scan"
            >
              {t("growth_scan_btn")} <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 text-white/80 hover:text-white transition-colors"
              data-testid="button-mobile-menu"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[280px] bg-[#0A1F14] border-l border-white/10 z-[70] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img src={wMark} alt="Wrkt Digital logo" width={656} height={657} className="h-7 w-auto object-contain" />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1 text-white/60 hover:text-white transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-col gap-6 p-6 flex-1">
                {navItems.map((item) => (
                  <Link
                    key={item.page}
                    href={item.href}
                    className={mobileLinkClass(item.page)}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <div className="p-6 border-t border-white/10 space-y-4">
                <div
                  onClick={toggleLanguage}
                  className="flex items-center gap-1 text-xs tracking-widest font-semibold cursor-pointer transition-colors uppercase"
                >
                  <span className={nl ? "text-white" : "text-white/30"}>NL</span>
                  <span className="text-white/20 mx-0.5">/</span>
                  <span className={!nl ? "text-white" : "text-white/30"}>EN</span>
                </div>

                <button
                  onClick={() => { setLocation(`${prefix}/scan`); setMobileOpen(false); }}
                  className="btn btn--gradient btn--lg w-full"
                  data-testid="button-nav-scan-mobile"
                >
                  {t("growth_scan_btn")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
